const FOCUSABLE_SELECTORS =
    'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"]), [role="button"], [role="menuitemradio"], [role="menuitem"]';

/**
 * Упрощённая версия isFocusableElement. Мы уже знаем, что тег элемента соответствует тем,
 * на которые можно сфокусироваться
 * @param el
 */
export const isFocusableElementSimple = (el: HTMLElement): boolean =>
    !(el as HTMLElement & { disabled?: boolean }).disabled &&
    !el.closest('[aria-disabled="true"], [aria-hidden="true"]') &&
    !(el.dataset.withoutFocus === "true");

/**
 * Проверяет, что на элемент можно сфокусироваться
 * @param el
 */
export const isFocusableElement = (el: HTMLElement): boolean =>
    el.matches(FOCUSABLE_SELECTORS) && isFocusableElementSimple(el);

/**
 * Получает все фокусируемые элементы, внутри передаваемого элемента
 * @param {HTMLElement} element - передаваемый элемент
 * @returns {HTMLElement[]} - массив фокусируемых элементов
 */
export const getKeyboardFocusableElements = (
    element: HTMLElement,
): HTMLElement[] =>
    Array.from(
        element.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS),
    ).filter(isFocusableElement);

/**
 * Проверяем, есть ли у переданного элемента признаки "выбранного"
 * @param element
 */
export const isSelectedElement = (element: HTMLElement): boolean =>
    element.getAttribute("aria-checked") === "true" ||
    element.getAttribute("aria-selected") === "true" ||
    element.getAttribute("aria-pressed") === "true" ||
    element.getAttribute("aria-current") === "true";

/**
 * Проверяем, есть ли у переданного элемента специальный флаг
 * @param element
 */
export const isSelectedElementByFlag = (element: HTMLElement): boolean =>
    element.getAttribute("data-is-active") === "true";

/**
 * Получаем высоту контейнера относительно того числа элементов, которые мы хотим отобразить
 * @param maxVisibleElements - максимальное количество элементов, которые мы хотим отобразить
 * @param registeredChildren - массив всех отрендеренных дочерних элементов дропдауна, нужен для расчета высоты
 */
export const calcScrollHeightByElementsCount = (
    maxVisibleElements: number,
    registeredChildren?: HTMLElement[],
) => {
    /**
     * Если у контейнера есть хоть какие-то марджины, необходимо увеличить высоту на их модульную сумму
     */
    const items = registeredChildren;

    if (items) {
        let prevElementBottomY = -1;
        let height = 0;
        let lastElementRect = {
            height: 0,
            gap: 0,
        };
        for (let i = 0; i < maxVisibleElements; i++) {
            const item = items[i];

            let elementGap = 0;
            let elementHeight = 0;

            if (item) {
                // Если элемент есть, то используем данные для вычисления размеров и отступа
                const rect = item.getBoundingClientRect();
                elementHeight = rect.height;
                if (prevElementBottomY !== -1) {
                    // Расстояние между элементами получаем путём разницы верхнего Y текущего элемента и нижнего Y предыдущего элемента
                    elementGap = rect.y - prevElementBottomY;
                }
                prevElementBottomY = rect.y + rect.height;

                lastElementRect = {
                    height: elementHeight,
                    gap: elementGap,
                };
            } else {
                // Иначе используем данные последнего элемента
                elementHeight = lastElementRect.height;
                elementGap = lastElementRect.gap;
            }

            if (i === maxVisibleElements - 1) {
                elementGap /= 2;
            }
            height += elementHeight + elementGap;
        }
        return height;
    }

    return 0;
};
